import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import type { ContentViewerSession } from '@/content-viewer/store/schemas'
import type { AppDispatch } from '@/store/store'
import { fetchAndStoreUserFiles } from '@/user_files/files/utils/fetch-and-store-user-files'
import type { MedSummaryReference } from '@/medical-billing-summaries/references/store/schemas'
import { buildMedSummaryViewerSession } from './build-med-summary-viewer-session'
import type { MedSummarySourceRef } from './schemas'

export type OpenMedSummarySourceViewerOptions = {
  reference?: MedSummaryReference
  highlights?: PaxtonHighlightArea[]
  startingPage?: number
  // Asset surface, non-PDF source: open the stored conversion (AI-94) instead of the original
  convertedPdf?: { assetId: string; userFileId: string }
}

export type OpenMedSummarySourceViewerResult =
  | {
      opened: true
      session: ContentViewerSession
    }
  | {
      opened: false
      error: string
    }

type OpenMedSummarySourceViewerArgs = {
  source: MedSummarySourceRef
  options?: OpenMedSummarySourceViewerOptions
  dispatch: AppDispatch
}

export async function openMedSummarySourceViewer(args: OpenMedSummarySourceViewerArgs): Promise<OpenMedSummarySourceViewerResult> {
  const { source, options = {}, dispatch } = args

  // Converted-PDF sessions resolve their bytes by (assetId, userFileId) at render time -
  // no user-file record needed (the title comes from the source ref).
  if (options.convertedPdf) {
    const session = buildMedSummaryViewerSession({
      source,
      reference: options.reference,
      highlights: options.highlights,
      startingPage: options.startingPage,
      convertedPdf: options.convertedPdf,
    })
    return session ? { opened: true, session } : { opened: false, error: 'Could not load this source file.' }
  }

  // Always re-fetch rather than trusting a possibly-stale cached record: a file loaded
  // before it was deleted would otherwise open a session for content that no longer exists.
  const records = await fetchAndStoreUserFiles([source.source_id], dispatch)
  const userFile = records[source.source_id]

  const session = buildMedSummaryViewerSession({
    source,
    reference: options.reference,
    userFile,
    highlights: options.highlights,
    startingPage: options.startingPage,
  })

  return session ? { opened: true, session } : { opened: false, error: 'Could not load this source file.' }
}
